import { Routes } from '@angular/router';
import { canActivateAuth } from '@foxera/guards/auth-guard';
import { canActivateCatalog, canActivateCatalogRedirect, canActivateEmployeeRedirect, canActivateEmployees, canActivateInvoices, canActivatePayments, canActivateSettings, canActivateStatistics, canActivateWorkoutPlans } from '@foxera/guards/permission.guard';
import { canActivateTenant } from '@foxera/guards/tenant-guard';
import { canActivateTenantSelection } from '@foxera/guards/tenant-selection.guard';
import { ApplicationRoutes } from '@foxera/shared/constants/application.routes';

export const routes: Routes = [
  {
    path: ApplicationRoutes.TENANT_SELECTION,
    loadComponent: () =>
      import(
        '@foxera/feature/tenant/pages/tenant-selection/tenant-selection.component'
      ).then((m) => m.TenantSelectionComponent),
    canActivate: [canActivateAuth, canActivateTenantSelection],
  },
  {
    path: ApplicationRoutes.DASHBOARD,
    loadComponent: () =>
      import('@foxera/shared/pages/coming-soon/coming-soon.component').then(
        (m) => m.ComingSoonComponent
      ),
    canActivate: [canActivateAuth, canActivateTenant],
  },
  {
    path: ApplicationRoutes.SCHEDULE,
    loadComponent: () =>
      import('@foxera/feature/schedule/pages/schedule-view/schedule-view.component').then(
        (m) => m.ScheduleViewComponent
      ),
    canActivate: [canActivateAuth, canActivateTenant],
  },
  {
    path: ApplicationRoutes.EMPLOYEE,
    loadComponent: () =>
      import('@foxera/shared/pages/coming-soon/coming-soon.component').then(
        (m) => m.ComingSoonComponent
      ),
    canActivate: [canActivateAuth, canActivateTenant, canActivateEmployeeRedirect],
  },
  {
    path: ApplicationRoutes.EMPLOYEE_TEAM_MEMBERS,
    loadComponent: () =>
      import(
        '@foxera/feature/employee/pages/employee-search/employee-search.component'
      ).then((m) => m.EmployeeSearchComponent),
    canActivate: [canActivateAuth, canActivateTenant, canActivateEmployees],
  },
  {
    path: ApplicationRoutes.EMPLOYEE_SCHEDULE_SHIFTS,
    loadComponent: () =>
      import(
        '@foxera/feature/employee/pages/schedule-shifts/schedule-shifts.component'
      ).then((m) => m.ScheduleShiftsComponent),
    canActivate: [canActivateAuth, canActivateTenant, canActivateEmployees],
  },
  {
    path: ApplicationRoutes.CATALOG,
    loadComponent: () =>
      import('@foxera/shared/pages/coming-soon/coming-soon.component').then(
        (m) => m.ComingSoonComponent
      ),
    canActivate: [canActivateAuth, canActivateTenant, canActivateCatalogRedirect],
  },
  {
    path: ApplicationRoutes.CATALOG_SERVICES,
    loadComponent: () =>
      import(
        '@foxera/feature/catalog/pages/services-view/services.component'
      ).then((m) => m.ServicesComponent),
    canActivate: [canActivateAuth, canActivateTenant, canActivateCatalog],
  },
  {
    path: ApplicationRoutes.CATALOG_MEMBERSHIPS,
    loadComponent: () =>
      import(
        '@foxera/feature/catalog/pages/memberships-view/memberships.component'
      ).then((m) => m.MembershipsComponent),
    canActivate: [canActivateAuth, canActivateTenant, canActivateCatalog],
  },
  {
    path: ApplicationRoutes.CLIENTS,
    loadComponent: () =>
      import('@foxera/feature/clients/pages/clients-view/clients.component').then(
        (m) => m.ClientsComponent
      ),
    canActivate: [canActivateAuth, canActivateTenant],
  },
  {
    path: `${ApplicationRoutes.CLIENTS_DETAIL}/:tab`,
    loadComponent: () =>
      import('@foxera/feature/clients/pages/client-general-info/client-general-info.component').then(
        (m) => m.ClientGeneralInfoComponent
      ),
    canActivate: [canActivateAuth, canActivateTenant],
  },
  {
    path: ApplicationRoutes.CLIENTS_DETAIL,
    redirectTo: `${ApplicationRoutes.CLIENTS_DETAIL}/personal`,
    pathMatch: 'full'
  },
  {
    path: ApplicationRoutes.ACTIVE_PLANS,
    loadComponent: () =>
      import('@foxera/feature/activeplans/pages/active-plans/active-plans.component').then(
        (m) => m.ActivePlansComponent
      ),
    canActivate: [canActivateAuth, canActivateTenant, canActivateWorkoutPlans],
  },
  {
    path: ApplicationRoutes.STATISTICS,
    loadComponent: () =>
      import('@foxera/shared/pages/coming-soon/coming-soon.component').then(
        (m) => m.ComingSoonComponent
      ),
    canActivate: [canActivateAuth, canActivateTenant, canActivateStatistics],
  },
  {
    path: ApplicationRoutes.STATISTICS_INVOICES,
    loadComponent: () =>
      import('@foxera/feature/invoices/pages/invoices-view/invoices-view.component').then(
        (m) => m.InvoicesViewComponent
      ),
    canActivate: [canActivateAuth, canActivateTenant, canActivateInvoices],
  },
  {
    path: ApplicationRoutes.STATISTICS_PAYMENTS,
    loadComponent: () =>
      import('@foxera/feature/invoices/pages/payments-view/payments-view.component').then(
        (m) => m.PaymentsViewComponent
      ),
    canActivate: [canActivateAuth, canActivateTenant, canActivatePayments],
  },
  {
    path: ApplicationRoutes.INVOICES,
    redirectTo: ApplicationRoutes.STATISTICS_PAYMENTS,
    pathMatch: 'full',
  },
  {
    path: ApplicationRoutes.SETTINGS,
    loadComponent: () =>
      import(
        '@foxera/feature/settings/pages/settings-view/settings.component'
      ).then((m) => m.SettingsComponent),
    canActivate: [canActivateAuth, canActivateTenant, canActivateSettings],
  },
  {
    path: ApplicationRoutes.SETTINGS_BRANCHES,
    loadComponent: () =>
      import(
        '@foxera/feature/settings/pages/branches-view/branches.component'
      ).then((m) => m.BranchesComponent),
    canActivate: [canActivateAuth, canActivateTenant, canActivateSettings],
    runGuardsAndResolvers: 'always',
  },
  {
    path: ApplicationRoutes.SETTINGS_GROUPS,
    loadComponent: () =>
      import(
        '@foxera/feature/settings/pages/groups-view/groups.component'
      ).then((m) => m.GroupsComponent),
    canActivate: [canActivateAuth, canActivateTenant, canActivateSettings],
  },
  {
    path: ApplicationRoutes.SETTINGS_EQUIPMENT,
    loadComponent: () =>
      import(
        '@foxera/feature/settings/pages/equipment-view/equipment.component'
      ).then((m) => m.EquipmentComponent),
    canActivate: [canActivateAuth, canActivateTenant, canActivateSettings],
  },
  {
    path: ApplicationRoutes.SETTINGS_BRANDING,
    loadComponent: () =>
      import(
        '@foxera/feature/settings/pages/branding-view/branding.component'
      ).then((m) => m.BrandingComponent),
    canActivate: [canActivateAuth, canActivateTenant, canActivateSettings],
  },
  {
    path: ApplicationRoutes.COMING_SOON,
    loadComponent: () =>
      import('@foxera/shared/pages/coming-soon/coming-soon.component').then(
        (m) => m.ComingSoonComponent
      ),
  },
  {
    path: ApplicationRoutes.NOT_FOUND,
    loadComponent: () =>
      import('@foxera/shared/pages/not-found/not-found.component').then(
        (m) => m.NotFoundComponent
      ),
  },
  // On empty route, go to employee/team-members
  {
    path: '',
    redirectTo: ApplicationRoutes.SCHEDULE,
    pathMatch: 'full',
  },
  // On any other non-existing route, show 404 page
  {
    path: '**',
    loadComponent: () =>
      import('@foxera/shared/pages/not-found/not-found.component').then(
        (m) => m.NotFoundComponent
      ),
  },
];
